"use strict";
/**
 * NEWBORN LABORATORY BILLING — one definition, for the counter, the ledger, the
 * lab order and the printed report.
 *
 * Desk, 2026-10-06: "under laboratory billing need new born check box ... it
 * should ask name of the baby, days, gender ... this should be passed on the lab
 * report ... new born babies have test but not create IP or OP for this babies
 * ... but lab report required under baby info only."
 *
 * THE SHAPE OF THE FACT. A newborn is never registered, so there is no patient
 * row, no OP number and no admission of their own. The test is billed to the
 * MOTHER's OP number or IP bill (that is the only anchor the money can sit on),
 * and the baby's identity travels beside the bill as three plain facts — a name,
 * an age in whole days, and a gender. Nothing here creates a person.
 *
 * WHY THE AGE IS STORED AS A BIRTH DATE, NOT AS "3". Reference ranges for a
 * newborn move by the day (bilirubin, for one), and a sample is often drawn the
 * morning after the bill. "3 days at billing" has to become "4 days at
 * collection" by itself, so the lab order keeps the calendar date of birth
 * derived once — from the moment the operator typed the age — and every later
 * question ("how old at collection?", "what range applies?") is answered from
 * that date. `bornOnFrom` and `newbornAgeDaysAt` are the two halves.
 *
 * ALL CALENDAR ARITHMETIC IS IN INDIA's CALENDAR (UTC+05:30, no DST). The
 * hospital's day is the IST day; a bill raised at 00:30 IST is still "today" to
 * the ward, though UTC calls it yesterday.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NEWBORN_GENDERS = exports.NEWBORN_NAME_MAX = exports.NEWBORN_NAME_MIN = exports.NEWBORN_MAX_AGE_DAYS = void 0;
exports.normalizeNewbornName = normalizeNewbornName;
exports.newbornFieldsState = newbornFieldsState;
exports.newbornFieldsProblem = newbornFieldsProblem;
exports.readNewborn = readNewborn;
exports.istDateKey = istDateKey;
exports.bornOnFrom = bornOnFrom;
exports.newbornAgeDaysAt = newbornAgeDaysAt;
exports.newbornAgeLabel = newbornAgeLabel;
exports.newbornSourceSuffix = newbornSourceSuffix;
exports.newbornGroupKey = newbornGroupKey;
/**
 * The oldest a "newborn" may be on this screen. The clinical newborn period is 28
 * days; the limit is wider because a preterm baby or a late first visit is still
 * a baby with no registration, and this is a typo guard ("300"), not a clinical
 * definition. One constant, so the browser and the server cannot disagree.
 */
exports.NEWBORN_MAX_AGE_DAYS = 90;
/** A baby's name as the desk types it ("Baby of Sunita"). */
exports.NEWBORN_NAME_MIN = 2;
exports.NEWBORN_NAME_MAX = 120;
/** The same three words the walk-in gender box offers; stored verbatim. */
exports.NEWBORN_GENDERS = ['Male', 'Female', 'Other'];
/** Whitespace collapsed and trimmed — the form the name is stored, compared and keyed in. */
function normalizeNewbornName(name) {
    return (name ?? '').replace(/\s+/g, ' ').trim();
}
const isGender = (value) => typeof value === 'string' && exports.NEWBORN_GENDERS.includes(value);
/**
 * NONE — the bill is not a newborn's. COMPLETE — all three present and valid.
 * PARTIAL — anything in between, which is always a defect: a name with no age
 * would print a baby with no age, an age with no name would print nobody.
 */
function newbornFieldsState(fields) {
    const name = normalizeNewbornName(fields.newbornName);
    const hasName = name.length > 0;
    const hasAge = fields.newbornAgeDays !== null && fields.newbornAgeDays !== undefined;
    const hasGender = Boolean(fields.newbornGender && String(fields.newbornGender).trim());
    if (!hasName && !hasAge && !hasGender)
        return 'NONE';
    const valid = name.length >= exports.NEWBORN_NAME_MIN &&
        name.length <= exports.NEWBORN_NAME_MAX &&
        hasAge &&
        Number.isInteger(fields.newbornAgeDays) &&
        fields.newbornAgeDays >= 0 &&
        fields.newbornAgeDays <= exports.NEWBORN_MAX_AGE_DAYS &&
        isGender(fields.newbornGender);
    return valid ? 'COMPLETE' : 'PARTIAL';
}
/** The sentence a refusal carries, or null when the fields are fine (complete, or all absent). */
function newbornFieldsProblem(fields) {
    return newbornFieldsState(fields) === 'PARTIAL'
        ? `A newborn needs the baby's name, age in days (0 to ${exports.NEWBORN_MAX_AGE_DAYS}) and gender — all three, or none.`
        : null;
}
/** The complete identity, or null when the fields are absent OR incomplete (use `newbornFieldsState` to tell which). */
function readNewborn(fields) {
    if (newbornFieldsState(fields) !== 'COMPLETE')
        return null;
    return {
        name: normalizeNewbornName(fields.newbornName),
        ageDays: fields.newbornAgeDays,
        gender: fields.newbornGender,
    };
}
// ---------------------------------------------------------------------------
// India's calendar
// ---------------------------------------------------------------------------
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
/** The calendar date in India at an instant, as `YYYY-MM-DD`. */
function istDateKey(at) {
    return new Date(at.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);
}
const dayNumber = (key) => Math.floor(Date.parse(`${key}T00:00:00.000Z`) / DAY_MS);
const keyOfDayNumber = (n) => new Date(n * DAY_MS).toISOString().slice(0, 10);
/**
 * The date of birth implied by "N days old" typed at an instant: today's India
 * date minus N. A baby of 0 days was born today.
 */
function bornOnFrom(typedAt, ageDays) {
    return keyOfDayNumber(dayNumber(istDateKey(typedAt)) - ageDays);
}
/**
 * Whole days of life at an instant, from a stored birth date (a `@db.Date`
 * arrives as UTC midnight, or as its `YYYY-MM-DD` text). Never negative: a
 * collection stamped before the implied birth date (a clock a day apart) reads
 * as day 0, not as a baby who has not been born.
 */
function newbornAgeDaysAt(bornOn, at) {
    const born = typeof bornOn === 'string' ? bornOn.slice(0, 10) : bornOn.toISOString().slice(0, 10);
    return Math.max(0, dayNumber(istDateKey(at)) - dayNumber(born));
}
/** "0 Days", "1 Day", "12 Days" — the form printed on the report and the label. */
function newbornAgeLabel(days) {
    return `${days} ${days === 1 ? 'Day' : 'Days'}`;
}
// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------
/** 32-bit FNV-1a as 8 hex digits — a short, stable fingerprint, not a security hash. */
function fnv1a(text) {
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i += 1) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
}
/**
 * THE SUFFIX THAT KEEPS A BABY'S CHARGE FROM COLLAPSING INTO THE MOTHER'S.
 *
 * An IP charge's idempotency key is `admission : service : service date`, and a
 * second capture of the same key is treated as a retry and returns the first
 * row. A mother's CBC and her baby's CBC on one day are the same key — the
 * baby's test would vanish without a word, unbilled and never ordered. Adding
 * this to the key makes the two different charges, while a genuine retry (same
 * baby) still produces the same key. Twins named differently are different
 * charges too; twins given the SAME name are the documented one-per-day trade
 * the key already makes for any test.
 *
 * Short on purpose: `sourceId` is capped at 120 characters and the base key is
 * already 84.
 */
function newbornSourceSuffix(name, gender) {
    return `:NB:${fnv1a(`${normalizeNewbornName(name).toLowerCase()}|${gender.trim().toUpperCase()}`)}`;
}
/**
 * Which baby a charge or an order belongs to — name, gender and DATE OF BIRTH,
 * so two entries typed on either side of midnight ("3 days" at 23:59, "4 days"
 * at 00:01) still read as the same baby, and two different babies on the same
 * admission never share a laboratory order. `null` for an ordinary charge.
 */
function newbornGroupKey(fields) {
    const name = normalizeNewbornName(fields.name);
    if (!name || !fields.gender || !fields.bornOn)
        return null;
    return `${name.toLowerCase()}|${fields.gender.trim().toUpperCase()}|${fields.bornOn.slice(0, 10)}`;
}
//# sourceMappingURL=lab-newborn.js.map